import { edgeGet, edgePost } from "@/api/rest";

export const ADMIN_EMAIL = "priyanka@gmail.com";

export type AdminMetricBlock = {
  total?: number;
  today?: number;
  week?: number;
  banned?: number;
  ready?: number;
  frames?: number;
  drifts?: number;
  drifts_today?: number;
  comments?: number;
  follows?: number;
  chat_messages_24h?: number;
  open_posts?: number;
  open_comments?: number;
  open_messages?: number;
  open_users?: number;
  total_open?: number;
  pending?: number;
  active?: number;
};

export type AdminFinanceBlock = {
  promo_inr_total_paise?: number;
  promo_inr_week_paise?: number;
  promo_inr_today_paise?: number;
  promo_sparks_total?: number;
  wallet_inr_total_paise?: number;
  wallet_inr_week_paise?: number;
  wallet_inr_today_paise?: number;
  wallet_coins_granted?: number;
  tips_coins_total?: number;
  tips_coins_week?: number;
  active_subscriptions?: number;
  wallet_balance_coins?: number;
};

export type AdminStats = {
  users?: AdminMetricBlock;
  posts?: AdminMetricBlock;
  engagement?: AdminMetricBlock;
  reports?: AdminMetricBlock;
  promotions?: AdminMetricBlock;
  finance?: AdminFinanceBlock;
  users_total?: number;
  posts_total?: number;
  pending_promotions?: number;
  active_promotions?: number;
  banned_users?: number;
};

export type AdminRevenueDay = {
  day: string;
  signups: number;
  wallet_inr_paise: number;
  promo_inr_paise: number;
  tips_coins: number;
};

export type AdminTopPost = {
  id: string;
  caption: string | null;
  created_at: string;
  is_drift: boolean;
  thumbnail_url: string | null;
  media_urls?: string[] | null;
  media?: unknown;
  video_metadata?: Record<string, unknown> | null;
  username: string | null;
  author_avatar_url?: string | null;
  vibe_count?: number;
  comment_count?: number;
  engagement?: number;
};

export type AdminFinanceTx = {
  id: string;
  tx_type: "wallet_topup" | "promotion" | "tip";
  created_at: string;
  user_id: string;
  username: string | null;
  amount_inr_paise: number;
  amount_coins: number;
  reference: string | null;
  status: string;
};

export type AdminPlatformInsights = {
  sparks?: {
    redemptions_total?: number;
    redemptions_pending?: number;
    redemptions_week?: number;
    sparks_spent_total?: number;
    pending_recharge_sparks?: number;
    ad_free_users?: number;
  };
  challenges?: {
    active_campaigns?: number;
    entries_total?: number;
    entries_week?: number;
    season_payout_sparks?: number;
    season_payout_users?: number;
  };
  creator_platform?: {
    tips_coins_total?: number;
    unlock_coins_total?: number;
    signal_impressions?: number;
    public_creators?: number;
    subscription_coins_total?: number;
  };
  push?: {
    device_tokens?: number;
    device_users?: number;
    sessions_active_7d?: number;
    outbox_pending?: number;
    outbox_24h?: number;
    outbox_week?: number;
  };
  parallel?: {
    sessions_total?: number;
    sessions_completed?: number;
    sessions_active?: number;
    sessions_week?: number;
    captures_total?: number;
    squad_groups?: number;
    squad_parallel_sessions?: number;
  };
  growth?: {
    contact_edges?: number;
    users_with_contacts?: number;
    phone_sync_users?: number;
    invite_outreach?: number;
    referrals_credited?: number;
    referrals_rejected?: number;
    referred_signups?: number;
  };
  admob?: {
    config_enabled?: boolean;
    ad_free_users_estimate?: number;
  };
};

export type AdminSparksRedemption = {
  id: string;
  user_id: string;
  username: string | null;
  reward_id: string;
  sparks_spent: number;
  status: string;
  created_at: string;
};

export type AdminSparksReward = {
  id: string;
  category: string;
  title: string;
  description: string | null;
  cost_sparks: number;
  metadata?: Record<string, unknown>;
  active: boolean;
  sort_order: number;
  redemption_count?: number;
};

export type AdminSeasonTier = {
  season_key: string;
  tier: number;
  rank_from: number;
  rank_to: number;
  sparks_reward: number;
};

export type AdminParallelSession = {
  id: string;
  status: string;
  member_count: number;
  created_at: string;
  updated_at: string;
  post_id: string | null;
  caption: string | null;
  moderation_status?: string | null;
  user_a_username: string | null;
  user_b_username: string | null;
  member_usernames: string | null;
  capture_count: number;
  thumbnail_url: string | null;
};

export type AdminAdminUser = {
  user_id: string;
  username: string | null;
  full_name: string | null;
  email: string | null;
  created_at: string;
};

export type AdminUser = {
  id: string;
  username: string | null;
  full_name: string | null;
  bio: string | null;
  website?: string | null;
  avatar_url: string | null;
  email: string | null;
  is_private: boolean;
  is_business: boolean;
  business_name: string | null;
  verified_creator?: boolean;
  sparks_balance?: number;
  wallet_balance_coins?: number;
  shadow_banned: boolean;
  banned_until: string | null;
  ban_reason: string | null;
  post_count?: number;
  frame_count?: number;
  drift_count?: number;
  parallel_session_count?: number;
  comment_count?: number;
  chat_message_count?: number;
  is_admin?: boolean;
  open_reports_against?: number;
  created_at: string;
};

export type AdminPost = {
  id: string;
  user_id: string;
  caption: string;
  type: string;
  status: string;
  moderation_status: string;
  audience: string;
  allow_comments: boolean;
  sensitive_content: boolean;
  is_drift: boolean;
  is_parallel?: boolean;
  has_media?: boolean;
  thumbnail_url: string | null;
  media_urls?: string[];
  media?: unknown;
  video_metadata?: Record<string, unknown> | null;
  username: string | null;
  full_name: string | null;
  author_avatar_url?: string | null;
  vibe_count?: number;
  comment_count?: number;
  signal_count?: number;
  bookmark_count?: number;
  unlock_count?: number;
  report_count?: number;
  media_row_count?: number;
  created_at: string;
};

export type AdminDataOverview = {
  users?: number;
  frames?: number;
  drifts?: number;
  parallels?: number;
  comments?: number;
  reactions?: number;
  signals?: number;
  captures?: number;
};

export type AdminCampaign = {
  id: string;
  advertiser_id: string;
  campaign_type: string;
  status: string;
  payment_method?: string | null;
  views_purchased: number;
  views_delivered: number;
  clicks?: number;
  budget_inr_paise?: number;
  sparks_spent?: number | null;
  package_price_paise?: number | null;
  target_scope: string;
  target_state: string | null;
  target_district: string | null;
  target_post_id?: string | null;
  target_thumbnail_url?: string | null;
  username: string | null;
  business_name: string | null;
  package_label: string | null;
  created_at: string;
};

export type AdminReport = {
  id: string;
  report_type: "post" | "comment" | "message" | "user";
  status: string;
  reason: string;
  details: string | null;
  created_at: string;
  target_id: string;
  subject_user_id: string;
  target_preview: string | null;
  thumbnail_url: string | null;
  media_urls?: string[] | null;
  is_drift: boolean;
  reporter_username: string | null;
  subject_username: string | null;
};

export type AdminActivity = {
  id: string;
  source: string;
  target_type: string;
  target_id: string;
  action: string;
  reason: string | null;
  created_at: string;
  actor_id: string | null;
  actor_username: string | null;
};

export type AdmobConfig = {
  enabled?: boolean;
  home_enabled?: boolean;
  drift_enabled?: boolean;
  chat_enabled?: boolean;
  home_native_interval?: number;
  drift_native_interval?: number;
  chat_banner_interval?: number;
  sponsored_home_interval?: number;
  sponsored_drift_interval?: number;
  premium_ad_free?: boolean;
};

export async function checkAdminAccess(): Promise<boolean> {
  const res = await edgePost<{ is_admin: boolean }>("admin-panel", {
    action: "check_access",
  });
  return res.is_admin === true;
}

export async function fetchAdminDashboard(): Promise<AdminStats> {
  const res = await edgePost<{ stats: AdminStats }>("admin-panel", {
    action: "dashboard",
  });
  return res.stats;
}

export async function fetchAdminRevenueTrend(days = 7): Promise<AdminRevenueDay[]> {
  const res = await edgePost<{ trend: AdminRevenueDay[] }>("admin-panel", {
    action: "revenue_trend",
    days,
  });
  return res.trend ?? [];
}

export async function fetchAdminTopPosts(
  kind?: "frame" | "drift",
  limit = 10,
): Promise<AdminTopPost[]> {
  const res = await edgePost<{ posts: AdminTopPost[] }>("admin-panel", {
    action: "top_posts",
    kind: kind ?? null,
    limit,
  });
  return res.posts ?? [];
}

export async function fetchAdminFinanceTransactions(limit = 30): Promise<AdminFinanceTx[]> {
  const res = await edgePost<{ transactions: AdminFinanceTx[] }>("admin-panel", {
    action: "finance_transactions",
    limit,
  });
  return res.transactions ?? [];
}

export async function fetchAdminPlatformInsights(): Promise<AdminPlatformInsights> {
  const res = await edgePost<{ insights: AdminPlatformInsights }>("admin-panel", {
    action: "platform_insights",
  });
  return res.insights ?? {};
}

export async function fetchAdminSparksRedemptions(
  status?: string,
  limit = 30,
): Promise<AdminSparksRedemption[]> {
  const res = await edgePost<{ redemptions: AdminSparksRedemption[] }>("admin-panel", {
    action: "list_sparks_redemptions",
    status: status || null,
    limit,
  });
  return res.redemptions ?? [];
}

export async function fetchAdminAdmins(): Promise<AdminAdminUser[]> {
  const res = await edgePost<{ admins: AdminAdminUser[] }>("admin-panel", {
    action: "list_admins",
  });
  return res.admins ?? [];
}

export async function fetchAdminSparksRewards(): Promise<AdminSparksReward[]> {
  const res = await edgePost<{ rewards: AdminSparksReward[] }>("admin-panel", {
    action: "list_sparks_rewards",
  });
  return res.rewards ?? [];
}

export async function updateAdminSparksReward(
  rewardId: string,
  patch: Record<string, unknown>,
): Promise<void> {
  await edgePost("admin-panel", {
    action: "update_sparks_reward",
    reward_id: rewardId,
    patch,
  });
}

export async function reviewAdminSparksRedemption(
  redemptionId: string,
  reviewAction: "fulfill" | "cancel",
): Promise<void> {
  await edgePost("admin-panel", {
    action: "review_sparks_redemption",
    redemption_id: redemptionId,
    review_action: reviewAction,
  });
}

export async function fetchAdminSeasonTiers(seasonKey?: string): Promise<AdminSeasonTier[]> {
  const res = await edgePost<{ tiers: AdminSeasonTier[] }>("admin-panel", {
    action: "list_season_tiers",
    season_key: seasonKey || null,
  });
  return res.tiers ?? [];
}

export async function updateAdminSeasonTier(
  seasonKey: string,
  tier: number,
  patch: Record<string, unknown>,
): Promise<void> {
  await edgePost("admin-panel", {
    action: "update_season_tier",
    season_key: seasonKey,
    tier,
    patch,
  });
}

export async function fetchAdminParallels(
  search: string,
  status?: string,
): Promise<AdminParallelSession[]> {
  const res = await edgePost<{ sessions: AdminParallelSession[] }>("admin-panel", {
    action: "list_parallels",
    search: search || null,
    status: status || null,
    limit: 50,
  });
  return res.sessions ?? [];
}

export type AdminPurgeResult = {
  parallel_sessions_deleted?: number;
  posts_deleted?: number;
  parallel_posts_deleted?: number;
};

export async function fetchAdminPurgeOrphans(): Promise<AdminPurgeResult> {
  const res = await edgePost<{ result: AdminPurgeResult }>("admin-panel", {
    action: "purge_orphans",
  });
  return res.result ?? {};
}

export async function deleteAdminParallel(sessionId: string): Promise<void> {
  await edgePost("admin-panel", {
    action: "delete_parallel",
    session_id: sessionId,
  });
}

export async function adjustAdminUserSparks(
  userId: string,
  delta: number,
  reason?: string,
): Promise<void> {
  await edgePost("admin-panel", {
    action: "adjust_user_sparks",
    user_id: userId,
    delta,
    reason: reason ?? null,
  });
}

export async function fetchAdminUsers(search: string): Promise<AdminUser[]> {
  const res = await edgePost<{ users: AdminUser[] }>("admin-panel", {
    action: "list_users",
    search: search || null,
    limit: 50,
  });
  return res.users ?? [];
}

export async function updateAdminUser(
  userId: string,
  patch: Record<string, unknown>,
): Promise<void> {
  await edgePost("admin-panel", {
    action: "update_user",
    user_id: userId,
    patch,
  });
}

export async function banAdminUser(
  userId: string,
  banned: boolean,
  reason?: string,
): Promise<void> {
  await edgePost("admin-panel", {
    action: "ban_user",
    user_id: userId,
    banned,
    reason: reason ?? null,
  });
}

export async function deleteAdminUser(userId: string): Promise<void> {
  await edgePost("admin-panel", {
    action: "delete_user",
    user_id: userId,
  });
}

export async function fetchAdminPosts(
  search: string,
  options?: { status?: string; kind?: "frame" | "drift" | "all" },
): Promise<AdminPost[]> {
  const res = await edgePost<{ posts: AdminPost[] }>("admin-panel", {
    action: "list_posts",
    search: search || null,
    status: options?.status || null,
    kind: options?.kind && options.kind !== "all" ? options.kind : null,
    limit: 50,
  });
  return res.posts ?? [];
}

export async function updateAdminPost(
  postId: string,
  patch: Record<string, unknown>,
): Promise<void> {
  await edgePost("admin-panel", {
    action: "update_post",
    post_id: postId,
    patch,
  });
}

export async function fetchAdminDataOverview(): Promise<AdminDataOverview> {
  const res = await edgePost<{ overview: AdminDataOverview }>("admin-panel", {
    action: "data_overview",
  });
  return res.overview ?? {};
}

export async function deleteAdminPost(postId: string): Promise<void> {
  await edgePost("admin-panel", {
    action: "delete_post",
    post_id: postId,
  });
}

export type AdminBulkDeleteResult = {
  deleted?: number;
  kind?: string;
  r2_deleted?: number;
};

export async function deleteAllAdminPosts(
  kind: "frame" | "drift" | "all",
): Promise<AdminBulkDeleteResult> {
  const res = await edgePost<{ result: AdminBulkDeleteResult; r2_deleted?: number }>(
    "admin-panel",
    { action: "delete_all_posts", kind },
  );
  return {
    ...(res.result ?? {}),
    r2_deleted: res.r2_deleted,
  };
}

export async function deleteAllAdminParallels(): Promise<AdminBulkDeleteResult> {
  const res = await edgePost<{ result: AdminBulkDeleteResult; r2_deleted?: number }>(
    "admin-panel",
    { action: "delete_all_parallels" },
  );
  return {
    ...(res.result ?? {}),
    r2_deleted: res.r2_deleted,
  };
}

export async function fetchAdminCampaigns(
  status?: string,
): Promise<AdminCampaign[]> {
  const res = await edgePost<{ campaigns: AdminCampaign[] }>("admin-panel", {
    action: "list_campaigns",
    status: status || null,
    limit: 50,
  });
  return res.campaigns ?? [];
}

export async function reviewAdminCampaign(
  campaignId: string,
  reviewAction: "approve" | "reject" | "suspend",
  reason?: string,
): Promise<void> {
  await edgePost("admin-panel", {
    action: "review_campaign",
    campaign_id: campaignId,
    review_action: reviewAction,
    reason: reason ?? null,
  });
}

export async function fetchAdminReports(
  reportType?: string,
  status = "open",
): Promise<AdminReport[]> {
  const res = await edgePost<{ reports: AdminReport[] }>("admin-panel", {
    action: "list_reports",
    report_type: reportType || null,
    status,
    limit: 50,
  });
  return res.reports ?? [];
}

export async function reviewAdminReport(
  reportId: string,
  reportType: AdminReport["report_type"],
  reviewAction: "dismiss" | "remove_content" | "ban_author" | "delete_user",
  reason?: string,
): Promise<void> {
  await edgePost("admin-panel", {
    action: "review_report",
    report_id: reportId,
    report_type: reportType,
    review_action: reviewAction,
    reason: reason ?? null,
  });
}

export type AdminEconomyOverview = {
  marketplace?: {
    active?: number;
    pending_payment?: number;
    sold?: number;
    auction_active?: number;
    sales_inr_paise?: number;
  };
  digital_shop?: {
    catalog_active?: number;
    catalog_purchasable?: number;
    owners_total?: number;
    purchases_paid?: number;
    purchases_inr_paise?: number;
  };
  creator_payouts?: {
    pending?: number;
    active?: number;
    suspended?: number;
    total?: number;
  };
  direct_payments?: Record<
    string,
    { paid_count?: number; paid_inr_paise?: number }
  >;
};

export type AdminMarketplaceListing = {
  id: string;
  listing_type: "buy_now" | "auction";
  status: string;
  title: string;
  description: string | null;
  price_paise: number;
  current_bid_paise: number | null;
  auction_ends_at: string | null;
  sold_at: string | null;
  created_at: string;
  post_id: string | null;
  inventory_item_id: string | null;
  seller_id: string;
  seller_username: string | null;
  high_bidder_id: string | null;
  high_bidder_username: string | null;
  buyer_id: string | null;
  buyer_username: string | null;
  bid_count: number;
};

export type AdminDigitalItem = {
  id: string;
  sku: string;
  kind: string;
  name: string;
  description: string | null;
  coin_price: number;
  price_inr_paise: number | null;
  purchasable: boolean;
  rarity_label: string | null;
  preview_url: string | null;
  asset_metadata: Record<string, unknown>;
  active: boolean;
  sort_order: number;
  created_at: string;
  owner_count: number;
  equipped_count: number;
};

export type AdminCreatorPayout = {
  user_id: string;
  razorpay_account_id: string | null;
  status: "pending" | "active" | "suspended";
  created_at: string;
  updated_at: string;
  username: string | null;
  display_name: string | null;
  verified_creator: boolean;
  paid_orders_count: number;
  paid_inr_paise: number;
};

export async function fetchAdminEconomyOverview(): Promise<AdminEconomyOverview> {
  const res = await edgePost<{ overview: AdminEconomyOverview }>("admin-panel", {
    action: "economy_overview",
  });
  return res.overview ?? {};
}

export async function fetchAdminMarketplaceListings(
  status?: string,
  listingType?: string,
  limit = 50,
): Promise<AdminMarketplaceListing[]> {
  const res = await edgePost<{ listings: AdminMarketplaceListing[] }>("admin-panel", {
    action: "list_marketplace_listings",
    status: status || null,
    listing_type: listingType || null,
    limit,
  });
  return res.listings ?? [];
}

export async function cancelAdminMarketplaceListing(listingId: string): Promise<void> {
  await edgePost("admin-panel", {
    action: "update_marketplace_listing",
    listing_id: listingId,
    patch: { status: "canceled" },
  });
}

export async function fetchAdminDigitalItems(
  kind?: string,
  active?: boolean,
): Promise<AdminDigitalItem[]> {
  const res = await edgePost<{ items: AdminDigitalItem[] }>("admin-panel", {
    action: "list_digital_items",
    kind: kind || null,
    active: active ?? null,
    limit: 100,
  });
  return res.items ?? [];
}

export async function updateAdminDigitalItem(
  itemId: string,
  patch: Record<string, unknown>,
): Promise<void> {
  await edgePost("admin-panel", {
    action: "update_digital_item",
    item_id: itemId,
    patch,
  });
}

export async function fetchAdminCreatorPayouts(
  status?: string,
  search?: string,
): Promise<AdminCreatorPayout[]> {
  const res = await edgePost<{ payouts: AdminCreatorPayout[] }>("admin-panel", {
    action: "list_creator_payouts",
    status: status || null,
    search: search || null,
    limit: 50,
  });
  return res.payouts ?? [];
}

export async function updateAdminCreatorPayout(
  userId: string,
  patch: Record<string, unknown>,
): Promise<void> {
  await edgePost("admin-panel", {
    action: "update_creator_payout",
    user_id: userId,
    patch,
  });
}

export async function fetchAdminActivity(): Promise<AdminActivity[]> {
  const res = await edgePost<{ activity: AdminActivity[] }>("admin-panel", {
    action: "list_activity",
    limit: 80,
  });
  return res.activity ?? [];
}

export async function fetchAdmobConfig(): Promise<AdmobConfig> {
  const res = await edgeGet<{ config: AdmobConfig }>("ads-config", {});
  return res.config ?? {};
}

export async function updateAdmobConfig(patch: AdmobConfig): Promise<void> {
  await edgePost("admin-panel", {
    action: "update_admob",
    patch,
  });
}

export function mediaUrl(url: string | null | undefined): string | null {
  if (!url) return null;
  if (url.startsWith("http")) return url;
  const cdn = import.meta.env.VITE_CDN_BASE_URL?.trim() || "https://media.daywia.com";
  return `${cdn.replace(/\/$/, "")}/${url.replace(/^\//, "")}`;
}
